// frontend/src/App.js
// ✅ GOOGLE OAUTH VERSION

import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { GoogleOAuthProvider } from '@react-oauth/google';

import { AuthProvider } from "./lib/useAuth";
import { CreditsProvider } from "./state/credits";
import { cleanupIdempotencyKeys } from "./lib/idempotency";

import Layout from "./components/Layout";
import RequireAuth from "./components/RequireAuth";

import Landing from "./pages/Landing";
import Home from "./pages/Home";
import Create from "./pages/Create";
import Gallery from "./pages/Gallery";
import Pricing from "./pages/Pricing";
import Profile from "./pages/Profile";
import Result from "./pages/Result";
import CreatorHub from "./pages/CreatorHub";
import CreatorDirectory from "./pages/CreatorDirectory";
import ManageStyles from "./pages/ManageStyles";
import ContentPolicy from "./pages/ContentPolicy";
import GenerationsByStyle from "./pages/GenerationsByStyle";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import PaymentSuccess from './pages/PaymentSuccess';

// Get Google Client ID from environment variable
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

export default function App() {
  if (!GOOGLE_CLIENT_ID) {
    console.error("[App] REACT_APP_GOOGLE_CLIENT_ID not set in environment variables");
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          fontFamily: "system-ui, sans-serif",
        }}
      >
        <div style={{ textAlign: "center", color: "#C73A20" }}>
          <h2>Configuration Error</h2>
          <p>Google Client ID not configured. Please check your environment variables.</p>
        </div>
      </div>
    );
  }

  // ✅ Cleanup expired idempotency keys on app startup
  React.useEffect(() => {
    cleanupIdempotencyKeys();
    console.log("[App] Cleaned up expired idempotency keys");
  }, []);

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <AuthProvider>
        <CreditsProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Navigate to="/landing" replace />} />
              <Route path="/landing" element={<Landing />} />

              <Route element={<Layout />}>
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/content-policy" element={<ContentPolicy />} />
                
                <Route path="/home" element={<RequireAuth><Home /></RequireAuth>} />
                <Route path="/create" element={<RequireAuth><Create /></RequireAuth>} />
                <Route path="/gallery" element={<RequireAuth><Gallery /></RequireAuth>} />
                <Route path="/creator-hub" element={<RequireAuth><CreatorHub /></RequireAuth>} />
                <Route path="/pricing" element={<RequireAuth><Pricing /></RequireAuth>} />
                <Route path="/profile" element={<RequireAuth><Profile /></RequireAuth>} />
                <Route path="/creator" element={<RequireAuth><CreatorHub /></RequireAuth>} />
                <Route path="/creator-directory" element={<RequireAuth><CreatorDirectory /></RequireAuth>} />
                <Route path="/manage-styles" element={<RequireAuth><ManageStyles /></RequireAuth>} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
                <Route
                  path="/creator/styles/:styleId"
                  element={
                    <RequireAuth>
                      <GenerationsByStyle />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/generations-by-style/:styleId"
                  element={
                    <RequireAuth>
                      <GenerationsByStyle />
                    </RequireAuth>
                  }
                />
                <Route path="/result/:jobId" element={<RequireAuth><Result /></RequireAuth>} />
                <Route path="/result" element={<Navigate to="/gallery" replace />} />
              </Route>

              <Route path="*" element={<Navigate to="/landing" replace />} />
            </Routes>
          </BrowserRouter>
        </CreditsProvider>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}